import { For, Show } from "solid-js";
import Text from "./Text";
import styles from "./CoordinateInput.module.css";

export interface Coordinate {
    x: number;
    y: number;
    z: number;
}

interface CoordinateInputProps {
    value: Coordinate;
    onChange: (next: Coordinate) => void;
    fractional?: boolean;
    unit?: string;
}

const AXES = [
    { axis: "x", label: "COORDINATE_X" },
    { axis: "y", label: "COORDINATE_Y" },
    { axis: "z", label: "COORDINATE_Z" },
] as const;

// Accepts what players copy out of the game: "-248814 64 -1201", F3's "XYZ: -248814.500 / 64.000 / -1201.500", a /tp line.
export function parseCoordinatePaste(text: string): Coordinate | null {
    const numbers = text.match(/-?\d+(?:\.\d+)?/g);
    if (!numbers || numbers.length !== 3) return null;
    const [worldX, worldY, worldZ] = numbers.map(Number);
    return { x: worldX, y: worldY, z: worldZ };
}

export default function CoordinateInput(props: CoordinateInputProps) {
    // Block coordinates floor, so -1201.5 is the block at -1202, not -1201.
    const toAxisValue = (raw: number) => props.fractional ? raw : Math.floor(raw);

    const pasteTriple = (event: ClipboardEvent) => {
        const pasted = parseCoordinatePaste(event.clipboardData?.getData("text") ?? "");
        if (!pasted) return;
        event.preventDefault();
        props.onChange({ x: toAxisValue(pasted.x), y: toAxisValue(pasted.y), z: toAxisValue(pasted.z) });
    };

    // A lone "-" or a cleared field reads as NaN; committing 0 there rewrites the box mid-typing, so wait for a number.
    const typeAxis = (axis: keyof Coordinate, input: HTMLInputElement) => {
        const typed = input.valueAsNumber;
        if (Number.isNaN(typed)) return;
        props.onChange({ ...props.value, [axis]: toAxisValue(typed) });
    };

    return (
        <div class={styles.row}>
            <For each={AXES}>{({ axis, label }) => (
                <label class={styles.field}>
                    <span class={styles.label}>
                        <Text val={label} />
                        <Show when={props.unit}><span class={styles.unit}>{props.unit}</span></Show>
                    </span>
                    <input type="number" step={props.fractional ? "0.001" : "1"}
                        value={props.value[axis]}
                        onPaste={pasteTriple}
                        onInput={(e) => typeAxis(axis, e.currentTarget)}
                        onBlur={(e) => { e.currentTarget.value = String(props.value[axis]); }} />
                </label>
            )}</For>
        </div>
    );
}
